// frontend/src/site/SiteContext.jsx
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { db } from '../services/firebase';

import { getSiteTheme, themeToVars } from './theme/getSiteTheme';
import { normalizeSiteConfig } from './lib/normalizeSiteConfig';

// ✅ ETAPA 03: modelo + seções fixas
import {
  getModelSections,
  resolveModelId as resolveModelIdFromSections,
} from './builder/siteSections';
import { getModelDefaults, FEATURED_LIMIT_DEFAULT } from './theme/siteModel';

const SiteCtx = createContext(null);
export const useSite = () => useContext(SiteCtx);

/* ===============================
   Utils para WhatsApp (fonte única)
================================= */
const digitsOnly = (s = '') => String(s).replace(/[^\d]/g, '');
const buildWaUrl = (display) => {
  const dd = digitsOnly(display);
  if (!dd) return '';
  const withDdi = dd.startsWith('55') ? dd : `55${dd}`;
  return `https://wa.me/${withDdi}`;
};

/* ==========================================================
   ✅ DEFAULT GLOBAL DO MICROSITE
   Agora: LOVABLE (pra ficar válido pra todos)
========================================================== */
const DEFAULT_SITE_CONFIG = {
  modelId: 'lovable', // ✅ antes era 'corretor'
  preset: 'humano_a',
  styleId: 'humano',
  paletteId: 'a',
};

/* ===============================
   Defaults do Builder (fallback)
   ✅ por MODELO (ETAPA 03)
================================= */

// Modelo "corretor"
const DEFAULT_BUILDER_CORRETOR = {
  modelId: 'corretor',
  estrutura: [
    { id: 'hero', ativo: true },
    { id: 'sobre', ativo: true },
    { id: 'imoveis', ativo: true },
    { id: 'servicos', ativo: true },
    { id: 'provaSocial', ativo: true },
    { id: 'ctaFinal', ativo: true },
    { id: 'rodape', ativo: true },
  ],
  conteudo: {
    hero: {
      titulo: '',
      subtitulo: '',
      frase: '',
      ctaPrimario: 'Falar no WhatsApp',
      ctaSecundario: 'Ver imóveis',
      mostrarCtaSecundario: true,
    },
    sobre: {
      titulo: 'Sobre mim',
      bio: '',
      tempoMercado: '',
      areaAtuacao: '',
      diferenciais: [],
    },
    imoveis: {
      titulo: 'Imóveis em destaque',
      subtitulo: 'Seleção de oportunidades',
      modo: 'recentes',
      quantidade: FEATURED_LIMIT_DEFAULT,
      ids: [],
      mostrarBotaoVerTodos: true,
      textoBotaoVerTodos: 'Ver todos os imóveis',
    },
    servicos: {
      titulo: 'Serviços',
      subtitulo: '',
      itens: [
        { titulo: 'Compra', descricao: '' },
        { titulo: 'Venda', descricao: '' },
        { titulo: 'Locação', descricao: '' },
        { titulo: 'Consultoria imobiliária', descricao: '' },
      ],
    },
    provaSocial: {
      titulo: 'O que dizem meus clientes',
      subtitulo: '',
      depoimentos: [],
    },
    ctaFinal: {
      titulo: 'Pronto para dar o próximo passo?',
      descricao: 'Fale comigo e receba um atendimento direto e estratégico.',
      textoBotao: 'Falar no WhatsApp',
      tipoAcao: 'whatsapp',
      link: '',
    },
    rodape: {
      texto: '',
      linksRapidos: true,
      mostrarRedes: true,
    },
  },
};

// Modelo "imobiliaria"
const DEFAULT_BUILDER_IMOBILIARIA = {
  modelId: 'imobiliaria',
  estrutura: getModelSections('imobiliaria'),
  conteudo: {
    hero: {
      titulo: '',
      subtitulo: '',
      frase: '',
      ctaPrimario: 'Falar no WhatsApp',
      ctaSecundario: 'Ver imóveis',
      mostrarCtaSecundario: true,
      mostrarFiltroNoHero: true,
    },
    destaques: {
      titulo: 'Imóveis em destaque',
      subtitulo: 'Seleção de oportunidades',
      modo: 'recentes',
      quantidade: FEATURED_LIMIT_DEFAULT,
      ids: [],
      mostrarBotaoVerTodos: true,
      textoBotaoVerTodos: 'Ver todos',
    },
    terrenos: {
      titulo: 'Terrenos',
      subtitulo: 'Lotes e terrenos disponíveis',
      quantidade: FEATURED_LIMIT_DEFAULT,
      mostrarBotaoVerTodos: true,
      textoBotaoVerTodos: 'Ver todos os terrenos',
    },
    ctaFinal: {
      titulo: 'Quer receber opções sob medida?',
      descricao: 'Fale comigo e eu te envio as melhores oportunidades.',
      textoBotao: 'Falar no WhatsApp',
      tipoAcao: 'whatsapp',
      link: '',
    },
    rodape: {
      texto: '',
      linksRapidos: true,
      mostrarRedes: true,
    },
  },
};

const DEFAULTS_BY_MODEL = {
  corretor: DEFAULT_BUILDER_CORRETOR,
  imobiliaria: DEFAULT_BUILDER_IMOBILIARIA,
  // ✅ lovable usa o mesmo “shape” de conteúdo do corretor por enquanto
  // (o template Lovable consome ctx.estrutura/ctx.conteudo)
  lovable: DEFAULT_BUILDER_CORRETOR,
};

// helpers
function isObj(v) {
  return v && typeof v === 'object' && !Array.isArray(v);
}
function asObj(v) {
  return isObj(v) ? v : {};
}
function asArr(v) {
  return Array.isArray(v) ? v : [];
}

function deepMergeDefaults(defaults, incoming) {
  const d = asObj(defaults);
  const i = asObj(incoming);

  const out = { ...d, ...i };
  Object.keys(d).forEach((k) => {
    const dv = d[k];
    const iv = i[k];

    if (isObj(dv) && isObj(iv)) out[k] = deepMergeDefaults(dv, iv);
    if (Array.isArray(dv)) out[k] = Array.isArray(iv) ? iv : dv;
  });

  return out;
}

/* ==========================================================
   ✅ Migração silenciosa: “Lovable por padrão”
   Regra:
   - se NÃO existe escolha explícita do template (siteTemplate ausente)
   - e NÃO tem builder/conteúdo estruturado salvo (siteBuilder/siteConteudo/siteEstrutura)
   - e o modelId está em "corretor" (legado/default antigo)
   então => assume LOVABLE para /u/:handle
========================================================== */
function shouldDefaultToLovable(userData = {}) {
  const d = asObj(userData);

  const hasExplicitTemplate = Boolean(String(d.siteTemplate || '').trim());

  if (hasExplicitTemplate) return false;

  const hasBuilder =
    isObj(d.siteBuilder) ||
    isObj(d.siteContent) ||
    isObj(d.siteConteudo) ||
    Array.isArray(d.siteEstrutura);

  if (hasBuilder) return false;

  // se o cara explicitamente escolheu imobiliaria, respeita
  const mid = String(d?.siteConfig?.modelId || '')
    .trim()
    .toLowerCase();
  if (mid === 'imobiliaria') return false;

  // legado: 'corretor' ou vazio -> default lovable
  if (!mid || mid === 'corretor') return true;

  return false;
}

function normalizeBuilderFromUserDoc(userData) {
  const data = asObj(userData);

  const modelIdRaw =
    data?.siteConfig?.modelId ||
    data?.siteBuilder?.modelId ||
    data?.siteBuilder?.model ||
    data?.siteContent?.modelId ||
    data?.siteConteudo?.modelId ||
    data?.modelId ||
    DEFAULT_SITE_CONFIG.modelId;

  // 🔥 resolveModelIdFromSections pode não conhecer "lovable"
  // então tratamos lovable antes:
  const modelId =
    String(modelIdRaw || '').toLowerCase() === 'lovable'
      ? 'lovable'
      : resolveModelIdFromSections(modelIdRaw);

  const DEFAULT = DEFAULTS_BY_MODEL[modelId] || DEFAULT_BUILDER_CORRETOR;

  const sb = asObj(data.siteBuilder || data.siteContent || {});
  const estrutura =
    asArr(sb.estrutura).length > 0
      ? asArr(sb.estrutura)
      : asArr(data.siteEstrutura).length > 0
        ? asArr(data.siteEstrutura)
        : DEFAULT.estrutura;

  const conteudoRaw =
    Object.keys(asObj(sb.conteudo)).length > 0
      ? asObj(sb.conteudo)
      : Object.keys(asObj(data.siteConteudo)).length > 0
        ? asObj(data.siteConteudo)
        : DEFAULT.conteudo;

  const conteudo = deepMergeDefaults(DEFAULT.conteudo, conteudoRaw);

  const estruturaSan = (Array.isArray(estrutura) ? estrutura : [])
    .filter(Boolean)
    .map((s) => ({
      ...s,
      id: String(s?.id || '').trim(),
      ativo: s?.ativo !== false,
    }))
    .filter((s) => !!s.id);

  const estruturaFinal = estruturaSan.length ? estruturaSan : DEFAULT.estrutura;

  return { modelId, estrutura: estruturaFinal, conteudo };
}

function sanitizeFeaturedLimit({ modelId, conteudo }) {
  const mid =
    String(modelId || '').toLowerCase() === 'lovable'
      ? 'lovable'
      : resolveModelIdFromSections(modelId);

  const cfg = asObj(conteudo);
  const limit = FEATURED_LIMIT_DEFAULT;

  if (mid === 'imobiliaria') {
    const d = asObj(cfg.destaques);
    const ids = asArr(d.ids).filter(Boolean).slice(0, limit);

    return {
      ...cfg,
      destaques: {
        ...d,
        quantidade: Math.min(Number(d.quantidade || limit), limit),
        ids,
        modo: ids.length
          ? d.modo === 'recentes'
            ? 'selecionados'
            : d.modo || 'selecionados'
          : d.modo || 'recentes',
      },
      terrenos: {
        ...asObj(cfg.terrenos),
        quantidade: Math.min(Number(cfg?.terrenos?.quantidade || limit), limit),
      },
    };
  }

  const im = asObj(cfg.imoveis);
  const ids = asArr(im.ids).filter(Boolean).slice(0, limit);

  return {
    ...cfg,
    imoveis: {
      ...im,
      quantidade: Math.min(Number(im.quantidade || limit), limit),
      ids,
      modo: ids.length
        ? im.modo === 'recentes'
          ? 'selecionados'
          : im.modo || 'selecionados'
        : im.modo || 'recentes',
    },
  };
}

export default function SiteProvider({ children, paramKey = 'handle' }) {
  const params = useParams();
  const raw = params?.[paramKey]; // 'handle' (microsite) ou 'slug' (lovable)
  const handle = (raw || '').toLowerCase();

  const [loading, setLoading] = useState(true);

  const [corretorId, setCorretorId] = useState(null);
  const [perfilSite, setPerfilSite] = useState(null);
  const [socioIds, setSocioIds] = useState([]);

  // ✅ Draft theme config (ETAPA 04)
  const [siteConfig, setSiteConfig] = useState(DEFAULT_SITE_CONFIG);

  // ✅ Builder
  const [modelId, setModelId] = useState(DEFAULT_SITE_CONFIG.modelId);
  const [estrutura, setEstrutura] = useState(
    DEFAULT_BUILDER_CORRETOR.estrutura
  );
  const [conteudo, setConteudo] = useState(DEFAULT_BUILDER_CORRETOR.conteudo);

  // ✅ Modo editor
  const [manualUid, setManualUid] = useState(null);

  // ✅ UX save
  const [savingBuilder, setSavingBuilder] = useState(false);
  const [builderError, setBuilderError] = useState('');
  const [builderSuccess, setBuilderSuccess] = useState('');

  // uid alvo do editor (importante!)
  const editingUid = useMemo(
    () => String(manualUid || corretorId || '').trim(),
    [manualUid, corretorId]
  );

  /* =========================================================
     1) Resolver dono do site pelo handle (microsite público)
  ========================================================= */
  useEffect(() => {
    let alive = true;
    if (manualUid) return;

    (async () => {
      setLoading(true);
      try {
        if (!handle) {
          if (alive) {
            setCorretorId(null);
            setPerfilSite(null);
            setSocioIds([]);
            setModelId(DEFAULT_SITE_CONFIG.modelId);
            setEstrutura(DEFAULT_BUILDER_CORRETOR.estrutura);
            setConteudo(DEFAULT_BUILDER_CORRETOR.conteudo);
            setSiteConfig(DEFAULT_SITE_CONFIG);
          }
          return;
        }

        const qs = await getDocs(
          query(
            collection(db, 'users'),
            where('siteHandle', '==', handle),
            where('siteEnabled', '==', true),
            limit(1)
          )
        );

        if (!alive) return;

        if (!qs.empty) {
          const snap = qs.docs[0];
          const data = snap.data();

          // 1) normaliza config
          let safeData = normalizeSiteConfig(data);

          // 2) ✅ aplica default global (lovable) para usuários que não escolheram nada
          if (shouldDefaultToLovable(safeData)) {
            safeData = {
              ...safeData,
              siteTemplate: 'lovable',
              siteConfig: {
                ...asObj(safeData.siteConfig),
                modelId: 'lovable',
              },
            };
          }

          setCorretorId(snap.id);
          setPerfilSite({ id: snap.id, ...safeData });

          // ✅ draft theme: merge com defaults
          setSiteConfig({
            ...DEFAULT_SITE_CONFIG,
            ...asObj(safeData?.siteConfig),
          });

          const sb = normalizeBuilderFromUserDoc(safeData);
          setModelId(sb.modelId);
          setEstrutura(sb.estrutura);

          const conteudoSan = sanitizeFeaturedLimit({
            modelId: sb.modelId,
            conteudo: sb.conteudo,
          });
          setConteudo(conteudoSan);
        } else {
          setCorretorId(null);
          setPerfilSite(null);
          setSocioIds([]);
          setModelId(DEFAULT_SITE_CONFIG.modelId);
          setEstrutura(DEFAULT_BUILDER_CORRETOR.estrutura);
          setConteudo(DEFAULT_BUILDER_CORRETOR.conteudo);
          setSiteConfig(DEFAULT_SITE_CONFIG);
        }
      } catch (e) {
        console.error('SiteProvider (resolver handle) =>', e);
        if (alive) {
          setCorretorId(null);
          setPerfilSite(null);
          setSocioIds([]);
          setModelId(DEFAULT_SITE_CONFIG.modelId);
          setEstrutura(DEFAULT_BUILDER_CORRETOR.estrutura);
          setConteudo(DEFAULT_BUILDER_CORRETOR.conteudo);
          setSiteConfig(DEFAULT_SITE_CONFIG);
        }
      } finally {
        alive && setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [handle, manualUid]);

  /* =========================================================
     2) Carregar sócios do dono do site (aceitos/ativos)
  ========================================================= */
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!corretorId) {
        if (alive) setSocioIds([]);
        return;
      }

      try {
        if (perfilSite?.socioIds && Array.isArray(perfilSite.socioIds)) {
          const arr = perfilSite.socioIds.filter(Boolean);
          if (alive) setSocioIds(arr);
          return;
        }
      } catch {}

      const accepted = ['accepted', 'aceito', 'ativo', 'active', 'approved'];
      try {
        const q1 = query(
          collection(db, 'socios'),
          where('criador', '==', corretorId),
          where('status', 'in', accepted)
        );
        const q2 = query(
          collection(db, 'socios'),
          where('convidado', '==', corretorId),
          where('status', 'in', accepted)
        );

        const [s1, s2] = await Promise.all([getDocs(q1), getDocs(q2)]);
        if (!alive) return;

        const ids = new Set();
        s1.forEach((d) => d.data()?.convidado && ids.add(d.data().convidado));
        s2.forEach((d) => d.data()?.criador && ids.add(d.data().criador));

        setSocioIds([...ids]);
      } catch (e) {
        console.warn(
          'SiteProvider (socios): seguindo sem sócios ->',
          e?.message || e
        );
        if (alive) setSocioIds([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [corretorId, perfilSite?.socioIds]);

  /* =========================================================
     2.5) ✅ API do Editor: Carregar config por UID
  ========================================================= */
  async function loadByUid(uid) {
    const id = String(uid || '').trim();
    if (!id) return;

    setLoading(true);
    setManualUid(id);
    setBuilderError('');
    setBuilderSuccess('');

    try {
      const ref = doc(db, 'users', id);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        setCorretorId(null);
        setPerfilSite(null);
        setSocioIds([]);
        setModelId(DEFAULT_SITE_CONFIG.modelId);
        setEstrutura(DEFAULT_BUILDER_CORRETOR.estrutura);
        setConteudo(DEFAULT_BUILDER_CORRETOR.conteudo);
        setSiteConfig(DEFAULT_SITE_CONFIG);
        setLoading(false);
        return;
      }

      let safeData = normalizeSiteConfig(snap.data());

      // ✅ mesmo comportamento no editor (se você abrir um uid legado)
      if (shouldDefaultToLovable(safeData)) {
        safeData = {
          ...safeData,
          siteTemplate: 'lovable',
          siteConfig: {
            ...asObj(safeData.siteConfig),
            modelId: 'lovable',
          },
        };
      }

      setCorretorId(id);
      setPerfilSite({ id, ...safeData });

      setSiteConfig({ ...DEFAULT_SITE_CONFIG, ...asObj(safeData?.siteConfig) });

      const sb = normalizeBuilderFromUserDoc(safeData);
      setModelId(sb.modelId);
      setEstrutura(sb.estrutura);

      const conteudoSan = sanitizeFeaturedLimit({
        modelId: sb.modelId,
        conteudo: sb.conteudo,
      });
      setConteudo(conteudoSan);
    } catch (e) {
      console.error('SiteProvider loadByUid =>', e);
      setBuilderError(e?.message || 'Erro ao carregar siteBuilder.');
    } finally {
      setLoading(false);
    }
  }

  function clearManual() {
    setManualUid(null);
  }

  /* =========================================================
     2.55) ✅ SAVE siteConfig (tema / modelo / paleta) — ETAPA 04
  ========================================================= */
  async function saveSiteThemeConfig(nextCfg) {
    const id = String(editingUid || '').trim();
    if (!id) throw new Error('Sem uid para salvar siteConfig.');

    const cfg = asObj(nextCfg || siteConfig);

    // ✅ resolve modelId com suporte a lovable
    const nextModel =
      String(cfg.modelId || '').toLowerCase() === 'lovable'
        ? 'lovable'
        : resolveModelIdFromSections(cfg.modelId || modelId);

    const cfgFinal = {
      ...DEFAULT_SITE_CONFIG,
      ...cfg,
      modelId: nextModel,
    };

    await setDoc(
      doc(db, 'users', id),
      {
        siteConfig: cfgFinal,
        // opcional: salva também o template no topo
        ...(cfgFinal.modelId === 'lovable' ? { siteTemplate: 'lovable' } : {}),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    setPerfilSite((prev) =>
      prev
        ? {
            ...prev,
            siteTemplate:
              cfgFinal.modelId === 'lovable'
                ? 'lovable'
                : prev.siteTemplate || '',
            siteConfig: cfgFinal,
          }
        : prev
    );
    setSiteConfig(cfgFinal);

    return { ok: true };
  }

  /* =========================================================
     2.6) ✅ SAVE Builder no Firestore (merge + limite 6)
  ========================================================= */
  async function saveBuilder(uid, nextEstrutura, nextConteudo) {
    const legacy = typeof uid === 'string' && uid.length > 10;

    const id = String((legacy ? uid : editingUid) || '').trim();
    if (!id) throw new Error('Sem uid para salvar siteBuilder.');

    const estr = legacy ? nextEstrutura : (nextEstrutura ?? estrutura);
    const cont = legacy ? nextConteudo : (nextConteudo ?? conteudo);

    setSavingBuilder(true);
    setBuilderError('');
    setBuilderSuccess('');

    try {
      const scMid = String(siteConfig?.modelId || modelId).toLowerCase();
      const mid =
        scMid === 'lovable' ? 'lovable' : resolveModelIdFromSections(scMid);

      const DEFAULT = DEFAULTS_BY_MODEL[mid] || DEFAULT_BUILDER_CORRETOR;

      const mergedConteudo = deepMergeDefaults(DEFAULT.conteudo, asObj(cont));
      const conteudoSan = sanitizeFeaturedLimit({
        modelId: mid,
        conteudo: mergedConteudo,
      });

      const nextEstr = (asArr(estr).length ? asArr(estr) : DEFAULT.estrutura)
        .filter(Boolean)
        .map((s) => ({
          ...s,
          id: String(s?.id || '').trim(),
          ativo: s?.ativo !== false,
        }))
        .filter((s) => !!s.id);

      const payload = {
        siteBuilder: {
          modelId: mid,
          estrutura: nextEstr.length ? nextEstr : DEFAULT.estrutura,
          conteudo: conteudoSan,
          updatedAt: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, 'users', id), payload, { merge: true });

      setModelId(mid);
      setEstrutura(payload.siteBuilder.estrutura);
      setConteudo(payload.siteBuilder.conteudo);

      setBuilderSuccess('Conteúdo do site salvo ✅');
      return { ok: true };
    } catch (e) {
      const msg = e?.message || 'Erro ao salvar conteúdo do site.';
      setBuilderError(msg);
      return { ok: false, error: msg };
    } finally {
      setSavingBuilder(false);
    }
  }

  /* =========================================================
     3) Telefone do dono (fallback)
  ========================================================= */
  const siteOwnerPhone = useMemo(() => {
    const p = perfilSite || {};
    return (
      p.perfil?.whatsapp ||
      p.whatsapp ||
      p.perfil?.telefone ||
      p.telefone ||
      p.contatos?.whatsapp ||
      null
    );
  }, [perfilSite]);

  /* =========================================================
     4) Link WhatsApp oficial do microsite (FONTE ÚNICA)
  ========================================================= */
  const ownerWhatsUrl = useMemo(() => {
    const p = perfilSite || {};
    const direct =
      p.whatsappUrl || p.perfil?.whatsappUrl || p.contatos?.whatsappUrl || null;

    if (direct && /^https?:\/\//i.test(direct)) return direct;

    const display =
      p.whatsappDisplay ||
      p.perfil?.whatsappDisplay ||
      p.contatos?.whatsappDisplay ||
      siteOwnerPhone ||
      '';

    return display ? buildWaUrl(display) : '';
  }, [perfilSite, siteOwnerPhone]);

  /* =========================================================
     5) Owners = dono + sócios (sem duplicatas)
  ========================================================= */
  const owners = useMemo(() => {
    const set = new Set([corretorId, ...(socioIds || [])].filter(Boolean));
    return [...set];
  }, [corretorId, socioIds]);

  const isReady = useMemo(
    () => !loading && Boolean(corretorId),
    [loading, corretorId]
  );

  /* =========================================================
     ✅ THEME (ETAPA 04) — AJUSTADO
  ========================================================= */
  const themeKey = useMemo(() => {
    const sc = asObj(siteConfig);
    return [
      sc.modelId || modelId,
      sc.styleId || '',
      sc.paletteId || '',
      sc.preset || '',
      sc.layoutDensity || '',
      sc.buttonStyle || '',
      sc.cardStyle || '',
    ].join('|');
  }, [
    siteConfig?.modelId,
    siteConfig?.styleId,
    siteConfig?.paletteId,
    siteConfig?.preset,
    siteConfig?.layoutDensity,
    siteConfig?.buttonStyle,
    siteConfig?.cardStyle,
    modelId,
  ]);

  const perfilForTheme = useMemo(() => {
    const p = asObj(perfilSite);

    // ✅ draft vence SEMPRE (editor)
    const mergedCfg = {
      ...DEFAULT_SITE_CONFIG,
      ...asObj(p.siteConfig),
      ...asObj(siteConfig),
    };

    // ✅ resolve com suporte a lovable
    const midRaw = String(mergedCfg.modelId || '').toLowerCase();
    mergedCfg.modelId =
      midRaw === 'lovable' ? 'lovable' : resolveModelIdFromSections(midRaw);

    return { ...p, siteConfig: mergedCfg };
  }, [perfilSite, siteConfig, themeKey]);

  const themeBase = useMemo(
    () => getSiteTheme(perfilForTheme),
    [perfilForTheme, themeKey]
  );

  const themeVars = useMemo(
    () => themeToVars(themeBase || {}),
    [themeBase, themeKey]
  );

  const theme = useMemo(
    () => ({
      ...(themeBase || { mode: 'light' }),
      cssVars: themeVars,
      _key: themeKey,
    }),
    [themeBase, themeVars, themeKey]
  );

  const modelDefaults = useMemo(() => getModelDefaults(modelId), [modelId]);

  const value = useMemo(
    () => ({
      loading,
      isReady,

      ownerId: corretorId,
      corretorId,
      siteHandle: handle,
      perfilSite,

      socioIds,
      owners,

      siteOwnerPhone,
      ownerWhatsUrl,

      // ✅ Theme draft (ETAPA 04)
      siteConfig,
      setSiteConfig,
      saveSiteThemeConfig,

      // ✅ Builder
      modelId,
      setModelId,
      modelDefaults,
      estrutura,
      setEstrutura,
      conteudo,
      setConteudo,

      // ✅ Editor API
      manualUid,
      loadByUid,
      clearManual,
      saveBuilder,

      // ✅ UX
      savingBuilder,
      builderError,
      builderSuccess,

      // ✅ Theme
      theme,
      themeVars,
      themeKey,
    }),
    [
      loading,
      isReady,
      corretorId,
      handle,
      perfilSite,
      socioIds,
      owners,
      siteOwnerPhone,
      ownerWhatsUrl,
      siteConfig,
      modelId,
      modelDefaults,
      estrutura,
      conteudo,
      manualUid,
      savingBuilder,
      builderError,
      builderSuccess,
      theme,
      themeVars,
      themeKey,
    ]
  );

  return (
    <SiteCtx.Provider value={value}>
      <div data-theme={theme.mode} style={themeVars} className="min-h-screen">
        {children}
      </div>
    </SiteCtx.Provider>
  );
}
